import React, { useEffect, useCallback, useRef } from "react";
import {
  ModalContent,
  CloseButton,
  AntiModal,
  ArrowButton,
  ModalFiller,
} from "./Modal.styles";
import { colors } from "../../utils/styled/_variables.styled";
import { ModalProps } from "./Modal.types";

export default function Modal({
  onClose,
  onReturn,
  children,
  returnDisabled,
  hideArrow,
  idText,
  header,
}: ModalProps & { header?: React.ReactNode }) {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];

    return Array.from(
      modalRef.current.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex='-1'])"
      )
    ).filter((el) => el.offsetParent !== null);
  }, []);

  const trapFocus = useCallback(
    (event: KeyboardEvent) => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.key === "Tab") {
        if (event.shiftKey) {
          if (activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    [getFocusableElements]
  );


  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "Tab") {
        trapFocus(event);
      }
    },
    [trapFocus, onClose]
  );

  useEffect(() => {
    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    closeButtonRef.current?.focus();

    document.body.style.overflow = "hidden";
    document.querySelector('body')?.setAttribute('aria-hidden', 'true');
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
      document.querySelector('body')?.removeAttribute('aria-hidden');
      previouslyFocusedElement.current?.focus();
    };
  }, [handleKeyDown]);

  return (
    <ModalFiller>
      <div
        tabIndex={0}
        onFocus={() => {
          const focusableElements = getFocusableElements();
          focusableElements[focusableElements.length - 1]?.focus();
        }}
      />

      <div
        className="modal__out"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idText}
      >
        <ModalContent id={idText}>
          <div className="modal__header-wrapper">
            <div
              className={`modal__header ${hideArrow ? "hideArrow" : ""}`}
            >
              {!hideArrow && (
                <ArrowButton
                  onClick={onReturn}
                  aria-label="Botão de retornar"
                  disabled={returnDisabled}
                  role="button"
                  className="buttonReturn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M24.7763 14.1235H9.88301L16.3897 7.61681C16.9097 7.09681 16.9097 6.24348 16.3897 5.72348C16.1406 5.47381 15.8024 5.3335 15.4497 5.3335C15.097 5.3335 14.7588 5.47381 14.5097 5.72348L5.72301 14.5101C5.20301 15.0301 5.20301 15.8701 5.72301 16.3901L14.5097 25.1768C15.0297 25.6968 15.8697 25.6968 16.3897 25.1768C16.9097 24.6568 16.9097 23.8168 16.3897 23.2968L9.88301 16.7901H24.7763C25.5097 16.7901 26.1097 16.1901 26.1097 15.4568C26.1097 14.7235 25.5097 14.1235 24.7763 14.1235Z"
                      fill={returnDisabled ? colors.white2 : colors.gray5}
                    />
                  </svg>
                </ArrowButton>
              )}
              <CloseButton
                onClick={onClose}
                aria-label="Botão de fechar o modal"
                role="button"
                ref={closeButtonRef}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M24.4009 7.61328C24.1518 7.3636 23.8136 7.22329 23.4609 7.22329C23.1082 7.22329 22.77 7.3636 22.5209 7.61328L16.0009 14.1199L9.48094 7.59994C9.23183 7.35027 8.89363 7.20996 8.54094 7.20996C8.18825 7.20996 7.85005 7.35027 7.60094 7.59994C7.08094 8.11994 7.08094 8.95994 7.60094 9.47994L14.1209 15.9999L7.60094 22.5199C7.08094 23.0399 7.08094 23.8799 7.60094 24.3999C8.12094 24.9199 8.96094 24.9199 9.48094 24.3999L16.0009 17.8799L22.5209 24.3999C23.0409 24.9199 23.8809 24.9199 24.4009 24.3999C24.9209 23.8799 24.9209 23.0399 24.4009 22.5199L17.8809 15.9999L24.4009 9.47994C24.9076 8.97328 24.9076 8.11994 24.4009 7.61328Z"
                    fill={colors.gray5}
                  />
                </svg>
              </CloseButton>
            </div>
            {header && header}
          </div>
          {children}
        </ModalContent>
      </div>
      <div
        tabIndex={0}
        onFocus={() => {
          const focusableElements = getFocusableElements();
          focusableElements[0]?.focus();
        }}
      />

      <AntiModal onClick={onClose} />
    </ModalFiller>
  );
}
